import type { ReactNode } from 'react';

/**
 * Выбор одного из двух-трёх коротких вариантов — парный выбор `.pick`.
 * Это форма, а не фильтр: при трёх и более вариантах или с пояснениями
 * берётся радио (кит, п. 10).
 */
export function ChoiceGroup<T extends string>({
  items,
  value,
  onChange,
}: {
  items: { value: T; label: string }[];
  value: T;
  onChange: (value: T) => void;
}) {
  return (
    <div className="pick">
      {items.map((i) => (
        <span
          key={i.value}
          className={i.value === value ? 'on' : ''}
          onClick={() => onChange(i.value)}
        >
          {i.label}
        </span>
      ))}
    </div>
  );
}

export function ChoiceField<T extends string>({
  label,
  ...rest
}: { label: string } & Parameters<typeof ChoiceGroup<T>>[0]) {
  return (
    <div className="field">
      <span className="lbl">{label}</span>
      <div>
        <ChoiceGroup {...rest} />
      </div>
    </div>
  );
}

/**
 * Строка выбора. Чекбокс — множественный выбор, радио — одиночный.
 * Полосы слева такая строка не получает: знак выбора здесь сам
 * графитовый чекбокс, вторая метка того же факта лишняя (кит, п. 2).
 */
export function Option({
  checked,
  onToggle,
  radio,
  disabled,
  title,
  description,
}: {
  checked: boolean;
  onToggle: () => void;
  radio?: boolean;
  disabled?: boolean;
  title: ReactNode;
  description?: ReactNode;
}) {
  const classes = ['opt'];
  if (radio) classes.push('rad');
  if (checked) classes.push('on');
  if (disabled) classes.push('dis');

  return (
    <div className={classes.join(' ')} onClick={disabled ? undefined : onToggle}>
      <span className="bx" />
      <span className="t">
        <span className="n">{title}</span>
        {description ? <span className="d">{description}</span> : null}
      </span>
    </div>
  );
}

/** Тумблер — для одной настройки (кит, п. 10). */
export function Switch({
  checked,
  onToggle,
  label,
  disabled,
}: {
  checked: boolean;
  onToggle: () => void;
  label: string;
  disabled?: boolean;
}) {
  const classes = ['swi'];
  if (checked) classes.push('on');
  if (disabled) classes.push('off');

  return (
    <div className={classes.join(' ')} onClick={disabled ? undefined : onToggle}>
      <span className="tr" />
      <span className="l">{label}</span>
    </div>
  );
}

/**
 * Чек-лист условий: пункты публикации в окне и на карточке.
 * `reqs` — чек-лист номера M3: круг, состояние цветом, у пункта кнопка
 * (стиль модуля M3, ограничен этим вариантом).
 */
export function ConditionList({ children, reqs }: { children: ReactNode; reqs?: boolean }) {
  return <div className={reqs ? 'chk reqs' : 'chk'}>{children}</div>;
}

export function ConditionItem({
  done,
  state,
  name,
  hint,
  action,
}: {
  done?: boolean;
  /** Четыре состояния пункта номера: выполнено · требует действия · не начато · не требуется */
  state?: 'done' | 'wait' | 'none' | 'na';
  name: string;
  hint?: ReactNode;
  action?: ReactNode;
}) {
  const s = state ?? (done ? 'done' : 'none');
  return (
    <div className={s === 'none' ? 'ci' : `ci ${s}`}>
      <span className="bx" />
      <div className="tt">
        <div className="n">{name}</div>
        {hint ? <div className="s">{hint}</div> : null}
      </div>
      {action}
    </div>
  );
}

/** Ссылка в тексте. */
export function TextLink({ onClick, children }: { onClick: () => void; children: ReactNode }) {
  return (
    <span className="lnk" onClick={onClick}>
      {children}
    </span>
  );
}

/**
 * Действие строки «снять» `.rmv` (кит 2.14, RowAction): тихий круглый
 * крестик, красный при наведении. Кнопка в колонке действия на телефоне
 * сворачивается до шеврона — строка открывает карточку, где действие
 * главное. У строки без карточки (дедлайн, позиция программы) другого
 * места нет, поэтому «снять» — крестиком, он остаётся и на телефоне.
 */
export function RowAction({ title, onClick }: { title: string; onClick: () => void }) {
  return (
    <span
      className="rmv"
      role="button"
      tabIndex={0}
      title={title}
      aria-label={title}
      onClick={(e) => {
        e.stopPropagation();
        onClick();
      }}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onClick();
        }
      }}
    >
      ✕
    </span>
  );
}

/**
 * Веер цветов — кандидат в кит (макет, `.fan`).
 *
 * Браслеты печатают, поэтому цвет выбирается из палитры, а не
 * набирается кодом: типография всё равно сведёт к своим краскам.
 * Колесо оставлено на случай, когда нужного оттенка в палитре нет.
 */
export const BAND_PALETTE = [
  '#9AA3AE', '#E08A3C', '#E17AA8', '#4A7BD4', '#8B6FD1', '#3FAE7A',
  '#E0C23C', '#D4574A', '#5BBFD6', '#B07A4E', '#7A8CA3', '#1F232B',
];

export function ColorFan({
  value,
  onChange,
}: {
  value: string;
  onChange: (color: string) => void;
}) {
  return (
    <div className="fan">
      {BAND_PALETTE.map((c) => (
        <span
          key={c}
          className={c.toUpperCase() === value.toUpperCase() ? 'sw on' : 'sw'}
          style={{ background: c }}
          onClick={() => onChange(c.toUpperCase())}
        />
      ))}
      <span className="sw wheel" style={{ background: value }}>
        <input type="color" value={value} onChange={(e) => onChange(e.target.value.toUpperCase())} />
      </span>
    </div>
  );
}

/** Точка цвета браслета рядом с названием. */
export function BandDot({ color }: { color: string }) {
  return <span className="bdot" style={{ background: color }} />;
}

/**
 * Строка состава пакета — кандидат в кит (макет, `.pitem`).
 *
 * Ближе всего `.opt`, но там чекбокс означает «выбрать из списка»,
 * а здесь — «что уже положено в пакет».
 */
export function PackageItem({
  mark,
  title,
  sub,
  onRemove,
  removeTitle = 'Убрать из состава',
}: {
  mark: string;
  title: ReactNode;
  sub?: ReactNode;
  onRemove?: () => void;
  removeTitle?: string;
}) {
  return (
    <div className="pitem">
      <span className="ic">{mark}</span>
      <div className="b">
        <div className="n">{title}</div>
        {sub ? <div className="s">{sub}</div> : null}
      </div>
      {onRemove ? (
        <span className="rmv" title={removeTitle} onClick={onRemove}>
          ✕
        </span>
      ) : null}
    </div>
  );
}
